import { renderInputComponent } from '@ticketmaster/tm1pos-web-shared/components/InputComponent';
import { renderTextAreaComponent } from '@ticketmaster/tm1pos-web-shared/components/TextareaComponent';
import { EMAIL, PRINT, SMS } from '@ticketmaster/tm1pos-web-shared/constants';
import { renderCountriesSelectionComponent } from '@ticketmaster/tm1pos-web-shared/containers/countriesDropdown';
import { type CountryCode, type CountryCodes, getCountryData } from '@ticketmaster/tm1pos-web-shared/countries';
import { selectFeatureFlags } from '@ticketmaster/tm1pos-web-shared/feature-flags/feature-flags-selector';
import { validateEmail } from '@ticketmaster/tm1pos-web-shared/utils/emailValidation';
import {
  normalizePhone,
  US_COUNTRY_ID,
  validatePhoneNumber as validatePhoneNumberUtil,
} from '@ticketmaster/tm1pos-web-shared/utils/phoneValidation';
import classNames from 'classnames';
import { formatIncompletePhoneNumber } from 'libphonenumber-js/max';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { change } from 'redux-form';
import { Field } from 'redux-form/immutable';
import { useAppSelector } from 'store/hooks';
import { ARCHTICS_EVENT_TYPE, ARCHTICS_NOTE_LIMITATION, HOST_NOTE_LIMITATION } from '../../../../../../constants';
import messages from '../../../../messages';
import { CARD, CHECKOUT_FORM_TITLE } from '../../constants';
import { selectDeliveryFormState } from './delivery-form-selector';
import EmailDeliveryForm from './email-delivery-form/email-delivery-form';
import TextSMSDeliveryForm from './text-sms-delivery-form/text-sms-delivery-form';
import type { InjectedIntlProps } from 'react-intl';

import './delivery-form.scss';

export const DeliveryControlFields = {
  COUNTRY_PREFIX: 'phonePre',
  DELIVERY_METHOD: 'deliveryMethod',
};

type DeliveryFormProps = InjectedIntlProps & {
  currentEvent: any;
  respectRotEntryToken: boolean;
  isManualEntry: boolean;
  fullNamePlaceholder: string;
  emailPlaceholder: string;
  notePlaceholder: string;
  phoneNumberPlaceholder: string;
  phonePre: any | null;
  deliveryMethod: string;
  paymentMethod: string;
};

export const DeliveryForm = ({
  fullNamePlaceholder,
  emailPlaceholder,
  notePlaceholder,
  respectRotEntryToken,
  currentEvent,
  phoneNumberPlaceholder,
  deliveryMethod,
  isManualEntry,
  paymentMethod,
  phonePre,
  intl,
}: DeliveryFormProps) => {
  const featureFlags = useAppSelector(selectFeatureFlags);

  const dispatch = useDispatch();
  const { locale, shouldRequireFullName, isEmvSupported } = useSelector(selectDeliveryFormState);
  const [countries, setCountries] = useState<CountryCodes>([]);
  const [defaultCountry, setDefaultCountry] = useState<CountryCode | null>(null);
  const countriesDOM = useRef<HTMLDivElement>(null);
  const isInitialized = useRef(false);

  const phonePrefix = useMemo(
    () => phonePre || { id: US_COUNTRY_ID, name: '+1', value: 'US', label: '+1' },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [phonePre?.id, phonePre?.name, phonePre?.value, phonePre?.label],
  );

  const limitedNumber = currentEvent.type === ARCHTICS_EVENT_TYPE ? ARCHTICS_NOTE_LIMITATION : HOST_NOTE_LIMITATION;

  const wrapperPadding = 64;

  const phoneNumberClassName = classNames(
    'sdr-checkout__input',
    'sdr-checkout--counties',
    'sdr-checkout__phoneNumbWrap',
  );

  const classNamePN = classNames('sdr-checkout__input', 'sdr-checkout__input--phoneNumber');

  const isFullNameRequired =
    (respectRotEntryToken && currentEvent.rotatingEntryTokenEnabled) ||
    (paymentMethod === CARD && isEmvSupported && (isManualEntry || shouldRequireFullName));

  const setDefaultPhonePre = useCallback(() => {
    const countryCode = currentEvent?.countryCode ? currentEvent?.countryCode : phonePrefix?.id;

    const newDefaultCountry =
      countries.find((country) => country.key === countryCode) ||
      countries.find((country) => country.key === US_COUNTRY_ID);

    setDefaultCountry(newDefaultCountry || null);

    return {
      id: newDefaultCountry?.key,
      value: newDefaultCountry?.key,
      label: `+${newDefaultCountry?.code}`,
      name: `+${newDefaultCountry?.code}`,
    };
  }, [countries, currentEvent?.countryCode, phonePrefix]);

  const setFormDefaultPhonePre = useCallback(
    (newPhonePre: any) => {
      if (dispatch) {
        dispatch(change(CHECKOUT_FORM_TITLE, 'phonePre', newPhonePre));
      }
    },
    [dispatch],
  );

  const getPhoneNumberErrorMessage = useCallback(
    (country: any) =>
      country.id === US_COUNTRY_ID
        ? intl.formatMessage(messages.phoneNumberValidationError)
        : intl.formatMessage(messages.phoneNumberValidationErrorNonUS),
    [intl],
  );

  const isPhoneNumberValid = useCallback((phoneNumber: string, country: any, isSmsDelivery: boolean) => {
    const isOptionalPhoneNumberBlank = !isSmsDelivery && !phoneNumber;

    if (isOptionalPhoneNumberBlank) {
      return true;
    }

    return validatePhoneNumberUtil(phoneNumber, country.id);
  }, []);

  const validatePhoneNumber = useCallback(
    (phoneNumber: string, formState: any): string | null => {
      const country = formState.get(DeliveryControlFields.COUNTRY_PREFIX) || phonePrefix;
      const isSmsDelivery = formState.get(DeliveryControlFields.DELIVERY_METHOD) === SMS;

      return isPhoneNumberValid(phoneNumber, country, isSmsDelivery) ? null : getPhoneNumberErrorMessage(country);
    },
    [getPhoneNumberErrorMessage, phonePrefix, isPhoneNumberValid],
  );

  const formatPhoneLocal = useCallback(
    (value: string) => formatIncompletePhoneNumber(value, phonePrefix?.id),
    [phonePrefix?.id],
  );

  const noteValidation = useCallback(
    (value: string) => {
      const limitation = currentEvent.type === ARCHTICS_EVENT_TYPE ? ARCHTICS_NOTE_LIMITATION : HOST_NOTE_LIMITATION;
      return (value && value.length > limitation) || undefined;
    },
    [currentEvent.type],
  );

  const validateEmailLocal = useCallback(
    (value: string, formState: any) => {
      const currentDeliveryMethod = formState.get(DeliveryControlFields.DELIVERY_METHOD);
      const linkGlobalAccount = formState.get('linkGlobalAccount');
      const isEmailValid = !validateEmail(value, currentDeliveryMethod === EMAIL || linkGlobalAccount);
      return isEmailValid ? undefined : intl.formatMessage(messages.emailNotValid);
    },
    [intl],
  );

  const valueIsNotEmpty = useCallback(
    (value: string) => (value ? undefined : intl.formatMessage(messages.required)),
    [intl],
  );

  const phoneNumberComponent = useMemo(
    () => (
      <div className={phoneNumberClassName} ref={countriesDOM}>
        <div className="sdr-checkout__form--fixed">
          <Field
            name={DeliveryControlFields.COUNTRY_PREFIX}
            component={renderCountriesSelectionComponent}
            type="text"
            defaultValue={defaultCountry}
            dataProvider={countries}
            height="25vh"
            width={countriesDOM.current?.offsetWidth ? countriesDOM.current.offsetWidth - wrapperPadding : undefined}
          />
        </div>
        <Field
          name="phoneNumber"
          className={classNamePN}
          component={renderInputComponent}
          format={formatPhoneLocal}
          placeholder={phoneNumberPlaceholder}
          type="text"
          normalize={normalizePhone}
          validate={validatePhoneNumber}
        />
      </div>
    ),
    [
      classNamePN,
      defaultCountry,
      formatPhoneLocal,
      phoneNumberClassName,
      phoneNumberPlaceholder,
      validatePhoneNumber,
      countries,
      wrapperPadding,
    ],
  );

  const fullNameComponent = useMemo(
    () => (
      <Field
        name="fullName"
        className="sdr-checkout__input sdr-checkout__input--fullname"
        component={renderInputComponent}
        placeholder={fullNamePlaceholder}
        validate={isFullNameRequired ? valueIsNotEmpty : undefined}
      />
    ),
    [fullNamePlaceholder, isFullNameRequired, valueIsNotEmpty],
  );

  const emailComponent = useMemo(
    () => (
      <Field
        name="email"
        className="sdr-checkout__input sdr-checkout__input--email"
        component={renderInputComponent}
        type="email"
        placeholder={emailPlaceholder}
        validate={validateEmailLocal}
      />
    ),
    [emailPlaceholder, validateEmailLocal],
  );

  const noteComponent = useMemo(
    () => (
      <Field
        name="paymentNode"
        className="sdr-checkout__paymentNode"
        noteLimited
        LimitedNumber={limitedNumber}
        component={renderTextAreaComponent}
        placeholder={notePlaceholder}
        validate={noteValidation}
      />
    ),
    [notePlaceholder, noteValidation, limitedNumber],
  );

  useEffect(() => {
    const fetchCountries = async () => {
      const countriesData = await getCountryData(locale);
      setCountries(countriesData);
    };

    fetchCountries();
  }, [locale]);

  useEffect(() => {
    if (countries.length > 0 && !isInitialized.current) {
      const newPhonePre = setDefaultPhonePre();
      setFormDefaultPhonePre(newPhonePre);
      isInitialized.current = true;
    }
  }, [countries, setDefaultPhonePre, setFormDefaultPhonePre]);

  return featureFlags?.designSystemCheckoutForm ? (
    <>
      {deliveryMethod === EMAIL && (
        <EmailDeliveryForm
          eventType={currentEvent.type}
          eventCountry={currentEvent?.countryCode}
          isFullNameRequired={isFullNameRequired}
        />
      )}
      {(deliveryMethod === PRINT || deliveryMethod === SMS) && (
        <TextSMSDeliveryForm
          eventType={currentEvent.type}
          eventCountry={currentEvent?.countryCode}
          isFullNameRequired={isFullNameRequired}
        />
      )}
    </>
  ) : (
    <>
      {deliveryMethod === EMAIL && (
        <div className="sdr-checkout__email-delivery">
          {emailComponent}
          {fullNameComponent}
          {phoneNumberComponent}
          {noteComponent}
        </div>
      )}
      {(deliveryMethod === PRINT || deliveryMethod === SMS) && (
        <div className="sdr-checkout__non-email-delivery">
          {phoneNumberComponent}
          {fullNameComponent}
          {emailComponent}
          {noteComponent}
        </div>
      )}
    </>
  );
};

export default injectIntl(DeliveryForm);
